import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './Leaderboard.css';

function Leaderboard() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(30);
  const [user, setUser] = useState(null);
  const [points, setPoints] = useState(0);

  useEffect(() => {
    // Get user from WebApp
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      const userData = tg.initDataUnsafe?.user;
      if (userData) {
        setUser(userData);
      }
    }

    loadData();
    loadUserPoints();
  }, []);

  const loadUserPoints = async () => {
    try {
      const veraRes = await api.get('/vera/balance');
      if (veraRes.data) {
        setPoints(veraRes.data.total_points || 0);
      }
    } catch (error) {
      console.error('Failed to load points:', error);
      setPoints(0);
    }
  };

  const loadData = async () => {
    try {
      const [leaderboardRes, statsRes] = await Promise.all([
        api.get('/leaderboard/top', { params: { limit: 100 } }),
        api.get('/leaderboard/stats')
      ]);

      setLeaderboard(leaderboardRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTelegramClick = () => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.HapticFeedback?.impactOccurred('light');
    }
    window.Telegram?.WebApp?.openTelegramLink('https://t.me/+wBXI4N49pwdhNDY1');
  };

  const getBadgeEmoji = (invites) => {
    if (invites >= 50) return '💎';
    if (invites >= 25) return '👑';
    if (invites >= 10) return '🏆';
    if (invites >= 5) return '🥇';
    if (invites >= 1) return '🥈';
    return '🥉';
  };

  const getBadgeName = (invites) => {
    if (invites >= 50) return 'Бриллиант';
    if (invites >= 25) return 'Легенда';
    if (invites >= 10) return 'Амбассадор';
    if (invites >= 5) return 'Активист';
    if (invites >= 1) return 'Участник';
    return 'Новичок';
  };

  const getInitials = (firstName) => {
    if (!firstName) return '?';
    return firstName.charAt(0).toUpperCase();
  };

  const getRankMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 30);
  };

  if (loading) {
    return (
      <div className="leaderboard-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Загрузка рейтинга...</p>
        </div>
      </div>
    );
  }

  const visibleUsers = leaderboard?.top_users.slice(0, displayCount) || [];
  const hasMore = leaderboard?.top_users.length > displayCount;

  return (
    <div className="leaderboard-page">
      {/* Header with Points */}
      <div className="leaderboard-header-top">
        <div
          className="points-display clickable"
          onClick={() => navigate('/profile')}
        >
          <span className="points-icon">⭐</span>
          <span className="points-value">{points}</span>
          <span className="points-label">VERA</span>
        </div>
        <div className="header-right">
          {user && (
            <div className="user-greeting">
              {user.first_name} 👋
            </div>
          )}
          <button className="telegram-chat-btn" onClick={handleTelegramClick} title="Присоединиться к чату">
            <svg className="telegram-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18.717-1.558 7.315-2.2 9.712-.271.996-.806 1.33-1.321 1.362-.563.035-1.175-.232-1.823-.58-1.011-.543-1.582-.88-2.563-1.41-1.133-.612-.399-1.013.247-1.601.169-.154 3.107-2.85 3.165-3.094.007-.031.014-.145-.054-.206s-.164-.04-.234-.024c-.1.024-1.688 1.073-4.764 3.149-.451.311-.86.462-1.226.454-.404-.009-1.18-.228-1.758-.416-.709-.23-1.272-.352-1.223-.743.025-.203.31-.41.855-.62 3.35-1.458 5.583-2.419 6.7-2.882 3.19-1.326 3.852-1.556 4.285-1.563.095-.002.307.022.445.134.116.094.148.221.163.31.015.088.034.29.019.447z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Title Section */}
      <div className="leaderboard-header-title">
        <h1>🏆 Рейтинг</h1>
        <p>Топ пользователей по приглашениям</p>
      </div>

      {/* Overall Stats */}
      {stats && (
        <div className="leaderboard-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total_users}</div>
            <div className="stat-label">Участников</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.total_invites}</div>
            <div className="stat-label">Приглашений</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.avg_invites_per_user}</div>
            <div className="stat-label">В среднем</div>
          </div>
        </div>
      )}

      {/* Current User Position (if not in top 3) */}
      {leaderboard?.current_user && leaderboard.current_user_rank > 3 && (
        <div className="current-user-card">
          <div className="current-user-header">
            <span className="current-user-title">Ваша позиция</span>
            <span className="current-user-rank">#{leaderboard.current_user_rank}</span>
          </div>
          <div className="current-user-content">
            <div className="user-avatar-small">
              {getInitials(leaderboard.current_user.first_name)}
            </div>
            <div className="current-user-info">
              <div className="current-user-name">
                {leaderboard.current_user.first_name || 'Вы'}
              </div>
              <div className="current-user-stats">
                👥 {leaderboard.current_user.invites_count} • ⭐ {leaderboard.current_user.vera_points}
              </div>
            </div>
            <div className="current-user-badge">
              {getBadgeEmoji(leaderboard.current_user.invites_count)}
            </div>
          </div>
        </div>
      )}

      {/* Compact Leaderboard List */}
      <div className="leaderboard-list-compact">
        {visibleUsers.map((user) => {
          const medal = getRankMedal(user.rank);
          return (
            <div
              key={user.telegram_id}
              className={`leaderboard-item-compact ${user.is_current_user ? 'current-user-item' : ''} ${user.rank <= 3 ? 'top-three' : ''}`}
            >
              <div className="rank-section">
                {medal ? (
                  <span className="rank-medal">{medal}</span>
                ) : (
                  <span className="rank-number">#{user.rank}</span>
                )}
              </div>

              <div className="user-avatar-compact">
                {getInitials(user.first_name)}
              </div>

              <div className="user-info-compact">
                <div className="user-name-compact">
                  {user.first_name || 'User'}
                  {user.username && <span className="username-compact">@{user.username}</span>}
                </div>
                <div className="user-stats-compact">
                  <span>👥 {user.invites_count}</span>
                  <span>⭐ {user.vera_points}</span>
                  <span>📅 {user.days_left}д</span>
                </div>
              </div>

              <div className="badge-section-compact">
                <span className="badge-emoji-compact">{getBadgeEmoji(user.invites_count)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="load-more-section">
          <button className="load-more-btn" onClick={handleLoadMore}>
            Показать еще
          </button>
        </div>
      )}

      {/* Call to Action */}
      <div className="leaderboard-cta">
        <div className="cta-icon">🎯</div>
        <div className="cta-text">
          <h3>Поднимайтесь в рейтинге!</h3>
          <p>Приглашайте друзей и зарабатывайте VERA очки</p>
        </div>
        <button
          className="cta-button"
          onClick={() => navigate('/invites')}
        >
          Пригласить друзей
        </button>
      </div>
    </div>
  );
}

export default Leaderboard;
